/**
 * IVF cycle context for chapter headers: cycle # → stim (med) day → days until retrieval or transfer.
 * Use formatIvfTimelineCallout() for the fertility chapter status pill; keep numbers in sync with stat.value when used in headers.
 */

/** Demo persona: stim days 1–8 of this cycle, ~9 days to retrieval (Cycle 2). */
export const DEMO_FERTILITY_IVF_TIMELINE = Object.freeze({
  cycleNumber: 2,
  stimDayStart: 1,
  stimDay: 8,
  countdownTarget: 'retrieval',
  daysUntil: 9,
  approximate: true,
});

export function daysUntilDate(isoDate) {
  if (!isoDate) return null;
  const end = new Date(`${isoDate}T23:59:59`);
  if (Number.isNaN(end.getTime())) return null;
  return Math.ceil((end - new Date()) / 86400000);
}

/**
 * @param {object|null} timeline
 * @returns {string|null}
 */
/**
 * Headline for the journey / IVF card when stimulation spans multiple days.
 * @param {number} stimDay
 * @param {number} [stimDayStart=1]
 */
export function formatStimulationRangeHeadline(stimDay, stimDayStart = 1) {
  if (stimDay == null || stimDay <= 0) return '';
  const start = stimDayStart != null && stimDayStart > 0 ? stimDayStart : 1;
  if (stimDay > start) return `Days ${start}–${stimDay} of stimulation`;
  return `Day ${stimDay} of stimulation`;
}

/** Short phrase for timeline callouts (status pill / stat line). */
export function formatStimDaysCalloutPhrase(stimDay, stimDayStart = 1) {
  if (stimDay == null || stimDay <= 0) return null;
  const start = stimDayStart != null && stimDayStart > 0 ? stimDayStart : 1;
  if (stimDay > start) return `Stim days ${start}–${stimDay}`;
  return `Stim day ${stimDay}`;
}

export function formatIvfTimelineCallout(timeline) {
  if (!timeline) return null;
  const parts = [];
  if (timeline.cycleNumber != null) parts.push(`Cycle ${timeline.cycleNumber}`);
  if (timeline.stimDay != null && timeline.stimDay > 0) {
    const phrase = formatStimDaysCalloutPhrase(timeline.stimDay, timeline.stimDayStart);
    if (phrase) parts.push(phrase);
  }
  if (timeline.daysUntil != null && timeline.countdownTarget) {
    const noun = timeline.countdownTarget === 'transfer' ? 'transfer' : 'retrieval';
    const val = timeline.approximate !== false ? `~${timeline.daysUntil}` : String(timeline.daysUntil);
    parts.push(`${val} days to ${noun}`);
  }
  return parts.length ? parts.join(' · ') : null;
}

/**
 * @param {object} currentCycle — Journey / useApp cycle shape
 * @param {object[]} cycles
 */
export function ivfTimelineFromCycle(currentCycle, cycles) {
  if (!currentCycle) return null;
  let cycleNumber = 1;
  if (Array.isArray(cycles) && cycles.length) {
    const idx = cycles.findIndex((c) => c && c.id === currentCycle.id);
    cycleNumber = idx >= 0 ? idx + 1 : cycles.length;
  }
  const stimDay =
    currentCycle.stimDay ??
    (currentCycle.startedAt
      ? Math.max(0, Math.floor((Date.now() - new Date(`${currentCycle.startedAt}T00:00:00`).getTime()) / 86400000))
      : null);
  const hasStimDay = stimDay != null && stimDay > 0;
  const stimDayStart =
    currentCycle.stimDayStart != null && currentCycle.stimDayStart > 0 ? currentCycle.stimDayStart : 1;
  const estRetrieval = currentCycle.estimatedRetrieval || currentCycle.retrievalDate || null;
  const transferDate = currentCycle.transferDate || null;
  const today = new Date();
  const retrievalTime = estRetrieval ? new Date(`${estRetrieval}T00:00:00`).getTime() : NaN;
  const retrievalPast = !Number.isNaN(retrievalTime) && today >= new Date(retrievalTime);

  let countdownTarget = 'retrieval';
  let daysUntil = daysUntilDate(estRetrieval);

  if (retrievalPast && transferDate) {
    const d = daysUntilDate(transferDate);
    if (d != null) {
      countdownTarget = 'transfer';
      daysUntil = d;
    }
  }

  return {
    cycleNumber,
    stimDay: hasStimDay ? stimDay : null,
    stimDayStart: hasStimDay ? stimDayStart : null,
    countdownTarget,
    daysUntil,
    approximate: true,
  };
}
